import React from "react";

// Brand tokens — shared across the Academy Hub + Live Room.
export const T = {
  bg: "#060606",
  surface: "#0C0C0C",
  panel: "#111111",
  border: "#1C1C1C",
  borderHover: "#2A2A2A",
  gold: "#C9A227",
  goldLight: "#E8B94F",
  goldGlow: "rgba(201,162,39,0.12)",
  text: "#EDE8DC",
  mute: "#AAAAAA",
  red: "#FF4444",
  emerald: "#00E5A0",
  blue: "#4A90E2",
  violet: "#B57BFF",
};

export const TYPE_COLOR = {
  FOREX: T.gold,
  CRYPTO: T.emerald,
  INDICES: T.blue,
  "AI TOOLS": T.violet,
  GENERAL: T.mute,
};

export const TYPE_ICON = {
  FOREX: "💱",
  CRYPTO: "₿",
  INDICES: "📊",
  "AI TOOLS": "🤖",
  GENERAL: "📺",
};

export const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const STATUS_PILL_COLOR = { scheduled: T.gold, cancelled: "#FF4444", none: T.mute };

export const ensureSessionId = (s) => (s.id ? s : { ...s, id: `s-${Math.random().toString(36).slice(2, 10)}` });

// Group a flat session list by day, preserving DAYS_OF_WEEK order.
export function groupSessionsByDay(rows) {
  const map = Object.fromEntries(DAYS_OF_WEEK.map((d) => [d, []]));
  for (const r of rows || []) {
    if (map[r.day]) map[r.day].push(r);
  }
  for (const d of DAYS_OF_WEEK) {
    map[d].sort((a, b) => (a.time_pht || "").localeCompare(b.time_pht || ""));
  }
  return DAYS_OF_WEEK.map((day) => ({ day, sessions: map[day] }));
}

// "YYYY-MM-DD" of THIS WEEK's occurrence of `weekday` (Monday-first).
export function dateForWeekday(weekday, baseDate = new Date()) {
  const dayIdx = DAYS_OF_WEEK.indexOf(weekday);
  if (dayIdx < 0) return null;
  const baseIdx = (baseDate.getDay() + 6) % 7;
  const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  d.setDate(d.getDate() + (dayIdx - baseIdx));
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Apply per-date overrides (cancel + add) on top of recurring schedule.
export function resolveDaySessions(baseSessions, overrides, dateStr) {
  const cancelById = new Map();
  const adds = [];
  for (const ov of overrides || []) {
    if (ov.date !== dateStr) continue;
    if (ov.type === "cancel" && ov.session_id) cancelById.set(ov.session_id, ov);
    else if (ov.type === "add") adds.push(ov);
  }
  const resolved = baseSessions.map((s) => {
    const ov = cancelById.get(s.id);
    if (!ov) return s;
    return { ...s, status: "cancelled", note: ov.note || s.note || "Cancelled for this date", _override_id: ov.id, _is_override_cancel: true };
  });
  for (const ov of adds) {
    resolved.push({
      id: `ov-${ov.id}`,
      day: null,
      time_pht: ov.time_pht || "TBD",
      title: ov.title || "One-off Session",
      type: ov.session_type || "FOREX",
      status: "scheduled",
      note: ov.note || "",
      description: ov.note || "",
      _override_id: ov.id,
      _is_one_off: true,
    });
  }
  resolved.sort((a, b) => (a.time_pht || "").localeCompare(b.time_pht || ""));
  return resolved;
}

export function extractYouTubeId(input) {
  if (!input) return "";
  const s = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
}

export function extractVimeoId(input) {
  if (!input) return "";
  const s = input.trim();
  if (/^\d{6,12}$/.test(s)) return s;
  const m = s.match(/vimeo\.com\/(?:video\/)?(\d{6,12})/);
  return m ? m[1] : "";
}

export function formatDate(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (!m) return "";
  return `${months[m - 1]} ${d}, ${y}`;
}

// ── style helpers ──────────────────────────────────────────────────
export const sectionTitle = {
  fontSize: 14, color: T.text, fontWeight: 700,
  fontFamily: "monospace", letterSpacing: 2, margin: "0 0 14px",
};
export const card = (opts = {}) => ({
  background: opts.background || T.surface,
  border: `1px solid ${opts.borderColor || T.border}`,
  borderLeft: opts.borderLeft || `1px solid ${opts.borderColor || T.border}`,
  borderRadius: 10,
  padding: opts.padding ?? 16,
  boxShadow: opts.boxShadow,
  overflow: opts.overflow,
  marginBottom: opts.marginBottom,
});
export const ctaBtn = (primary) => ({
  padding: "14px 20px", minHeight: 44, borderRadius: 8,
  background: primary ? `linear-gradient(135deg, ${T.gold}, ${T.goldLight})` : "transparent",
  border: primary ? "none" : `1px solid ${T.gold}`,
  color: primary ? "#0a0a0a" : T.gold,
  fontFamily: "monospace", fontSize: 12, fontWeight: 800, letterSpacing: 2,
  cursor: "pointer", textTransform: "uppercase",
});
export const input = {
  flex: 1, padding: "8px 10px", borderRadius: 6,
  background: T.panel, border: `1px solid ${T.border}`,
  color: T.text, fontSize: 13, outline: "none",
};
export const btn = ({ active, color }) => ({
  padding: "8px 14px", borderRadius: 6,
  background: active ? color + "22" : "transparent",
  border: `1px solid ${active ? color : T.border}`,
  color: active ? color : T.mute,
  cursor: "pointer", fontSize: 11, fontFamily: "monospace", letterSpacing: 1, fontWeight: 700,
});
export const muteHint = { fontSize: 10, color: T.mute, marginTop: 6, fontFamily: "monospace", letterSpacing: 0.5 };

export function FieldGroup({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
